<template>
  <div class="hmv" dir="rtl">

    <!-- Map tile -->
    <div class="hmv__map" ref="mapEl"></div>

    <!-- ── Top overlay: avatar (left) + search bar (right) ── -->
    <div class="hmv__top">
    <router-link to="/account" class="hmv__avatar" title="حسابي" aria-label="حسابي">
      <i v-if="!hasUser" class="fa fa-user"></i>
      <template v-else>{{ userInitial }}</template>
      <span v-if="unreadCount > 0" class="hmv__avatar-dot"></span>
    </router-link>

      <div class="hmv__search" :class="{ 'hmv__search--focus': searchFocused }">
        <i class="fa fa-search hmv__search-icon"></i>
        <input
          v-model="searchQuery"
          type="search"
          class="hmv__search-input"
          placeholder="ابحث عن مكان..."
          autocomplete="off"
          autocorrect="off"
          spellcheck="false"
          @focus="searchFocused = true"
          @blur="onSearchBlur"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="hmv__search-clear"
          @click="searchQuery = ''"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <!-- ── Category pills — top left ── -->
    <div class="hmv__pills">
      <button
        class="hmv__pill"
        :class="{ 'hmv__pill--on': !activeCategories.length }"
        @click="clearAllCategories"
        type="button"
      >
        الكل
        <span class="hmv__pill__count">{{ visibleBranches.length }}</span>
      </button>
      <button
        v-for="id in activeCategories"
        :key="id"
        class="hmv__pill hmv__pill--on"
        @click="toggleCategory(id)"
        type="button"
      >
        {{ categoryName(id) }}
        <i class="fa fa-times" style="font-size:0.55rem;opacity:0.7;margin-right:3px"></i>
      </button>
    </div>

    <!-- ── Right-side controls stack ── -->
    <div class="hmv__controls">

      <!-- Filter button -->
      <button
        class="hmv__filter"
        :class="{ 'hmv__filter--on': activeCategories.length }"
        @click="filterPanelOpen = true"
        type="button"
      >
        <i class="fa fa-sliders"></i>
        <span v-if="activeCategories.length" class="hmv__filter__badge">
          {{ activeCategories.length }}
        </span>
      </button>

      <!-- Count chip -->
      <div class="hmv__count">
        <i class="fa fa-location-dot"></i>
        {{ visibleBranches.length }}
      </div>

      <!-- Locate me -->
      <button
        v-if="locationState !== 'gps'"
        class="hmv__locate"
        @click="requestLocation"
        type="button"
      >
        <i class="fa" :class="locationState === 'loading' ? 'fa-spinner fa-spin' : 'fa-location-crosshairs'"></i>
      </button>

    </div>

    <!-- ── Empty-area hint — places exist but none in the current view ── -->
    <Transition name="pop">
      <button
        v-if="showEmptyHint"
        class="hmv__empty-hint"
        @click="zoomOutToAll"
        type="button"
      >
        <i class="fa fa-map-location-dot"></i>
        {{ emptyHintText }}
        <span class="hmv__empty-hint__cta">وسّع الخريطة</span>
      </button>
    </Transition>

    <!-- ── Single card — only when a marker/place is selected ── -->
    <Transition name="card-up">
      <div
        v-if="selected"
        class="hmv__card-wrap"
        :style="{ transform: `translateX(${dragX}px)` }"
        :class="{ 'hmv__card-wrap--dragging': dragging }"
        @touchstart.passive="onCardTouchStart"
        @touchmove.passive="onCardTouchMove"
        @touchend.passive="onCardTouchEnd"
      >
        <article class="hmv__card-single" @click="openBranch(selected)">
          <div class="hmv__card-img">
            <img :src="branchImage(selected)" @error="onImgError" />
          </div>
          <div class="hmv__card-info">
            <div class="hmv__card-top">
              <h3 class="hmv__card-name">{{ selected.company?.name }}</h3>
              <span v-if="selected.cycle_since" class="hmv__card-renewed">متجدد</span>
              <span
                v-if="hasHours(selected)"
                class="hmv__card-status"
                :class="selected.is_open ? 'hmv__card-status--open' : 'hmv__card-status--closed'"
              >{{ selected.is_open ? 'مفتوح' : 'مغلق' }}</span>
            </div>
            <p class="hmv__card-district">
              <i class="fa fa-map-marker"></i> {{ selected.district }} · {{ selected.city }}
            </p>
            <div class="hmv__card-foot">
              <span v-if="distanceLabel(selected)" class="hmv__card-dist">
                <i class="fa fa-location-arrow"></i> {{ distanceLabel(selected) }}
              </span>
              <span v-if="visibleBranches.length > 1" class="hmv__card-counter">
                {{ currentIndex + 1 }} / {{ visibleBranches.length }}
              </span>
            </div>
          </div>
        </article>
      </div>
    </Transition>

    <!-- ── Filter panel ── -->
    <Transition name="sheet-up">
      <div v-if="filterPanelOpen" class="hmv__fp" @click.self="filterPanelOpen = false">
        <div class="hmv__fp-sheet">

          <div class="hmv__fp-head">
            <span class="hmv__fp-title">
              التصفية
              <b v-if="activeCategories.length">{{ activeCategories.length }}</b>
            </span>
            <button class="hmv__fp-close" @click="filterPanelOpen = false" type="button">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="hmv__fp-mode">
            <button
              class="hmv__fp-mode-btn"
              :class="{ 'hmv__fp-mode-btn--on': strictMode }"
              @click="emit('update:strict-mode', true)"
              type="button"
            >كل التصنيفات</button>
            <button
              class="hmv__fp-mode-btn"
              :class="{ 'hmv__fp-mode-btn--on': !strictMode }"
              @click="emit('update:strict-mode', false)"
              type="button"
            >أي تصنيف</button>
          </div>

          <div class="hmv__fp-body">
            <div class="hmv__fp-groups">
              <button
                v-for="group in groups"
                :key="group.key"
                class="hmv__fp-group"
                :class="{ 'hmv__fp-group--on': fpActiveGroup?.key === group.key }"
                @click="fpActiveGroup = group"
                type="button"
              >
                <span v-if="countSelectedInGroup(group)" class="hmv__fp-group__badge">
                  {{ countSelectedInGroup(group) }}
                </span>
                <span class="hmv__fp-group__icon">{{ group.icon }}</span>
                <span class="hmv__fp-group__name">{{ group.name_ar }}</span>
              </button>
            </div>
            <div class="hmv__fp-cats">
              <template v-if="fpActiveGroup">
                <button
                  v-for="cat in fpActiveGroup.categories"
                  :key="cat.id"
                  class="hmv__fp-cat"
                  :class="{ 'hmv__fp-cat--on': activeCategories.includes(cat.id) }"
                  @click="toggleCategory(cat.id)"
                  type="button"
                >
                  <i :class="cat.icon"></i> {{ cat.name }}
                </button>
              </template>
              <p v-else class="hmv__fp-hint">اختر مجموعة من اليمين</p>
            </div>
          </div>

          <div class="hmv__fp-foot">
            <button v-if="activeCategories.length" class="hmv__fp-reset" @click="activeCategories = []; emit('clear')" type="button">
              مسح الكل
            </button>
            <button class="hmv__fp-apply" @click="applyFilters" type="button">
              عرض النتائج
              <span v-if="visibleBranches.length" class="hmv__fp-apply__n">{{ visibleBranches.length }}</span>
            </button>
          </div>

        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, onActivated, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useNotifications } from "@/composables/useNotifications";


const props = defineProps({
  branches:   { type: Array,   default: () => [] },
  groups:     { type: Array,   default: () => [] },
  strictMode: { type: Boolean, default: true },
});

const emit = defineEmits(["update:strict-mode", "toggle-category", "clear"]);

const router           = useRouter();
const mapEl            = ref(null);
const locationState    = ref("idle");
const userLocation     = ref(null);
const selected         = ref(null);
const mapCenter        = ref(null);
const activeCategories = ref([]);
const filterPanelOpen  = ref(false);
const fpActiveGroup    = ref(null);
const mapBounds        = ref(null);

// Top-bar overlay state
const searchQuery   = ref("");
const searchFocused = ref(false);

// Debounced copy of the query — visibleBranches (and the marker re-render
// behind it) reacts to this, so a burst of keystrokes collapses into one
// rebuild instead of one per key.
const debouncedQuery = ref("");
let queryTimer = null;
watch(searchQuery, (q) => {
  clearTimeout(queryTimer);
  queryTimer = setTimeout(() => { debouncedQuery.value = q; }, 200);
});

// Single-card swipe state
const dragX     = ref(0);
const dragging  = ref(false);

// Footer height — measured on mount, used by sheet positioning
const footerHeight = ref(70);

// Notifications
const { unreadCount, fetchUnread } = useNotifications();
onMounted(() => fetchUnread());


// User avatar initial from localStorage (guests get a user icon instead)
const cachedUser = (() => {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
})();
const hasUser     = computed(() => !!cachedUser?.name);
const userInitial = computed(() => cachedUser?.name?.charAt(0)?.toUpperCase() || "");

// Index of the selected branch within visibleBranches
const currentIndex = computed(() => {
  if (!selected.value) return -1;
  return visibleBranches.value.findIndex(b => b.id === selected.value.id);
});

let leafletMap   = null;
let markersLayer = null;
let L            = null;
let moveTimeout  = null;

const apiUrlImg = process.env.VUE_APP_API_URL_IMAGES || "";
const fallback  = `${apiUrlImg}/branches/defaultBranch.jpeg`;

const SAUDI_CENTER = { lat: 23.8859, lng: 45.0792 };
const SAUDI_ZOOM   = 6;

// ── Distance ──────────────────────────────────
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371, dLat = (lat2-lat1)*Math.PI/180, dLng = (lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function distanceFromPoint(branch, point) {
  if (!point || !branch.latitude || !branch.longitude) return null;
  return haversine(point.lat, point.lng, Number(branch.latitude), Number(branch.longitude));
}

function distanceLabel(branch) {
  const km = distanceFromPoint(branch, userLocation.value);
  if (!km) return "";
  return km < 1 ? `${Math.round(km * 1000)} م` : `${km.toFixed(1)} كم`;
}

// ── Visible branches ──────────────────────────
const visibleBranches = computed(() => {
  if (!props.branches.length) return [];
  let list;

  if (mapBounds.value && leafletMap) {
    list = props.branches
      .filter(b => b.latitude && b.longitude && mapBounds.value.contains([Number(b.latitude), Number(b.longitude)]))
      .map(b => ({ ...b, _km: distanceFromPoint(b, userLocation.value || mapCenter.value) }))
      .sort((a, b) => (a._km ?? 999) - (b._km ?? 999));
  } else {
    const center = mapCenter.value || userLocation.value;
    if (!center) return [];
    list = props.branches
      .filter(b => b.latitude && b.longitude)
      .map(b => ({ ...b, _km: distanceFromPoint(b, center) }))
      .filter(b => b._km !== null && b._km <= 25)
      .sort((a, b) => a._km - b._km);
  }

  if (activeCategories.value.length) {
    list = list.filter(b => {
      const ids = b.categories?.map(c => c.id) || [];
      return props.strictMode
        ? activeCategories.value.every(id => ids.includes(id))
        : activeCategories.value.some(id => ids.includes(id));
    });
  }

  // Search filter — name match (AR + EN), combined with category filter via AND.
  const q = debouncedQuery.value.trim().toLowerCase();
  if (q.length >= 2) {
    list = list.filter(b => {
      const c = b.company || {};
      const candidates = [
        c.name, c.name_ar, c.name_en,
        b.company_name_ar, b.company_name_en,
      ].filter(Boolean);
      return candidates.some(name => String(name).toLowerCase().includes(q));
    });
  }
  return list;
});

// ── Empty-area hint ───────────────────────────
// Places are loaded but none fall inside the current view (or match the
// active search/filters) — nudge the user to zoom out instead of showing
// a silently empty map.
const showEmptyHint = computed(() =>
  props.branches.length > 0 && !visibleBranches.value.length && !filterPanelOpen.value
);

const emptyHintText = computed(() =>
  (debouncedQuery.value.trim() || activeCategories.value.length)
    ? "لا توجد نتائج مطابقة هنا —"
    : "لا توجد أماكن هنا —"
);

function zoomOutToAll() {
  if (!leafletMap) return;
  leafletMap.flyTo([SAUDI_CENTER.lat, SAUDI_CENTER.lng], SAUDI_ZOOM, { duration: 0.8 });
}

// ── Helpers ───────────────────────────────────
function categoryName(id) {
  for (const g of props.groups) {
    const c = g.categories?.find(x => x.id === id);
    if (c) return c.name;
  }
  return String(id);
}

function countSelectedInGroup(group) {
  return group.categories?.filter(c => activeCategories.value.includes(c.id)).length || 0;
}

function hasHours(branch) {
  const h = branch.businessHours || branch.business_hours || [];
  return Array.isArray(h) && h.length && h.some(x => x.open || x.close);
}

function branchImage(branch) {
  if (branch?.imgs) return `${apiUrlImg}/branches/${branch.id}/${branch.imgs}`;
  return fallback;
}
function onImgError(e) { e.target.src = fallback; }

function openBranch(branch) {
  router.push({ name: "BranchDetails", params: { id: branch.id }, query: { entrySource: "map" } });
}

// Mobile browsers scroll the body to reveal the focused input above the
// keyboard, and leave it scrolled after blur — which pushes the top bar
// off-screen and opens a gap under the card. Snap back on blur.
function onSearchBlur() {
  searchFocused.value = false;
  window.scrollTo(0, 0);
}

// "الكل" pill — must also tell the parent to drop the server-side filter,
// otherwise the branches request stays filtered while the pills look clear.
function clearAllCategories() {
  if (!activeCategories.value.length) return;
  activeCategories.value = [];
  emit("clear");
}

// ── Filter ────────────────────────────────────
function toggleCategory(id) {
  activeCategories.value = activeCategories.value.includes(id)
    ? activeCategories.value.filter(x => x !== id)
    : [...activeCategories.value, id];
  emit("toggle-category", id);
  renderMarkers();
}

function applyFilters() {
  filterPanelOpen.value = false;
  renderMarkers();
}

// ── Select / navigate ─────────────────────────
function selectBranch(branch) {
  selected.value = branch;
  renderMarkers();
  // Pan map to the selected branch's marker
  if (leafletMap && branch.latitude && branch.longitude) {
    leafletMap.flyTo([Number(branch.latitude), Number(branch.longitude)], Math.max(leafletMap.getZoom(), 15), {
      duration: 0.4,
    });
  }
}

function selectByIndex(i) {
  const list = visibleBranches.value;
  if (!list.length) return;
  const idx = ((i % list.length) + list.length) % list.length; // wrap
  selectBranch(list[idx]);
}

function goNext() {
  if (currentIndex.value < 0) return;
  selectByIndex(currentIndex.value + 1);
}

function goPrev() {
  if (currentIndex.value < 0) return;
  selectByIndex(currentIndex.value - 1);
}

// ── Card swipe gestures ───────────────────────
let cardStartX = 0;
const SWIPE_THRESHOLD = 70;

function onCardTouchStart(e) {
  cardStartX    = e.touches[0].clientX;
  dragging.value = true;
  dragX.value    = 0;
}

function onCardTouchMove(e) {
  if (!dragging.value) return;
  // Rubber-band: only show partial drag movement (30% of finger travel)
  dragX.value = (e.touches[0].clientX - cardStartX) * 0.3;
}

function onCardTouchEnd(e) {
  if (!dragging.value) return;
  const delta = e.changedTouches[0].clientX - cardStartX;
  dragging.value = false;
  dragX.value    = 0; // snap back

  if (Math.abs(delta) < SWIPE_THRESHOLD) return;

  // RTL: swipe left (delta < 0) = next, swipe right (delta > 0) = prev
  if (delta < 0) goNext();
  else           goPrev();
}

// ── Map move ──────────────────────────────────
// Map move/zoom — fires on moveend/zoomend. Updates bounds + center;
// the watcher on visibleBranches re-renders markers automatically.
function onMapMove() {
  if (!leafletMap) return;
  mapBounds.value = leafletMap.getBounds();
  const c = leafletMap.getCenter();
  mapCenter.value = { lat: c.lat, lng: c.lng };
}

// ── Pin colors ────────────────────────────────
function pinColor(branch) {
  if (!hasHours(branch)) return "#E2B673";
  return branch.is_open ? "#8FA37A" : "#D86545";
}

// ── Leaflet CDN ───────────────────────────────
function loadLeaflet() {
  return new Promise(resolve => {
    if (window.L?.markerClusterGroup) { L = window.L; resolve(); return; }
    const css = hrefs => hrefs.forEach(href => {
      const el = document.createElement("link"); el.rel = "stylesheet"; el.href = href; document.head.appendChild(el);
    });
    css([
      "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css",
      "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css",
      "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css",
    ]);
    const s1 = document.createElement("script"); s1.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s1.onload = () => {
      const s2 = document.createElement("script"); s2.src = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js";
      s2.onload = () => { L = window.L; resolve(); };
      document.head.appendChild(s2);
    };
    document.head.appendChild(s1);
  });
}

// ── Init map ──────────────────────────────────
async function initMapSaudi() {
  await loadLeaflet();
  await nextTick();
  if (!mapEl.value || leafletMap) return;

  leafletMap = L.map(mapEl.value, { zoomControl: false, attributionControl: false })
    .setView([SAUDI_CENTER.lat, SAUDI_CENTER.lng], SAUDI_ZOOM);

  // OSM standard tiles — keyless; CARTO's free basemap now watermarks
  // "API KEY REQUIRED" into its tiles. The CSS filter keeps the muted look.
  L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  }).addTo(leafletMap);

  markersLayer = L.markerClusterGroup({
    showCoverageOnHover: false, zoomToBoundsOnClick: true,
    spiderfyOnMaxZoom: true, spiderfyDistanceMultiplier: 2.5,
    maxClusterRadius: 18, disableClusteringAtZoom: 14,
    iconCreateFunction: cluster => L.divIcon({
      className: "",
      html: `<div style="display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;background:rgba(26,22,18,0.92);border:1.5px solid rgba(226,182,115,0.6);font-family:system-ui;font-size:13px;font-weight:700;color:#E2B673;box-shadow:0 4px 16px rgba(0,0,0,0.35)">${cluster.getChildCount()}</div>`,
      iconSize: [36, 36], iconAnchor: [18, 18],
    }),
  }).addTo(leafletMap);

  // Single handler for both pan and zoom — both update bounds & center,
  // and the visibleBranches watcher takes it from there.
  leafletMap.on("moveend zoomend", onMapMove);

  // Tap on empty map area → deselect (card slides out, full-map view).
  leafletMap.on("click", () => {
    if (selected.value) {
      selected.value = null;
      renderMarkers();
    }
  });

  mapBounds.value = leafletMap.getBounds();
  mapCenter.value = { ...SAUDI_CENTER };
  renderMarkers();
}

// ── Add user location ─────────────────────────
function addUserLocationToMap(lat, lng, accuracy) {
  if (!leafletMap || !L) return;
  L.circle([lat, lng], {
    radius: Math.min(accuracy || 80, 300),
    color: "#E2B673", fillColor: "#E2B673", fillOpacity: 0.08, weight: 1, opacity: 0.30,
  }).addTo(leafletMap);
  L.marker([lat, lng], {
    icon: L.divIcon({
      className: "",
      html: `<div style="width:16px;height:16px;border-radius:50%;background:#E2B673;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.35),0 0 0 3px rgba(226,182,115,0.30)"></div>`,
      iconSize: [16, 16], iconAnchor: [8, 8],
    }),
    zIndexOffset: 2000,
  }).addTo(leafletMap);
}

function tryLocationSilently() {
  if (!navigator.geolocation) return;
  locationState.value = "loading";
  navigator.geolocation.getCurrentPosition(
    pos => {
      userLocation.value  = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      locationState.value = "gps";
      if (leafletMap && L) {
        addUserLocationToMap(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
        leafletMap.setView([pos.coords.latitude, pos.coords.longitude], 14, { animate: true });
        mapBounds.value = leafletMap.getBounds();
        renderMarkers();
      }
    },
    () => { locationState.value = "idle"; },
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
  );
}

function requestLocation() {
  if (!navigator.geolocation) return;
  locationState.value = "loading";
  navigator.geolocation.getCurrentPosition(
    pos => {
      userLocation.value  = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      locationState.value = "gps";
      if (leafletMap) {
        addUserLocationToMap(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
        leafletMap.setView([pos.coords.latitude, pos.coords.longitude], 14, { animate: true });
        mapBounds.value = leafletMap.getBounds();
        renderMarkers();
      }
    },
    () => { locationState.value = "idle"; },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
}

// ── Render markers ────────────────────────────
function renderMarkers() {
  if (!L || !markersLayer) return;
  markersLayer.clearLayers();
  visibleBranches.value.forEach(branch => {
    const color = pinColor(branch);
    const name  = branch.company?.name || "مكان";
    const sel   = selected.value?.id === branch.id;
    const icon  = L.divIcon({
      className: "",
      html: `<div style="display:inline-flex;align-items:center;gap:5px;background:${sel ? color : 'rgba(26,22,18,0.90)'};border:1.5px solid ${color};border-radius:999px;padding:5px 11px 5px 8px;font-family:system-ui;font-size:12px;font-weight:700;color:${sel ? '#1a1410' : '#F7F1E6'};white-space:nowrap;box-shadow:0 4px 16px rgba(0,0,0,0.28);cursor:pointer;transform:${sel ? 'scale(1.08)' : 'scale(1)'}"><span style="width:7px;height:7px;border-radius:50%;background:${color};flex-shrink:0"></span>${name}</div>`,
      iconAnchor: [0, 30], iconSize: [null, null],
    });
    const m = L.marker([Number(branch.latitude), Number(branch.longitude)], { icon, zIndexOffset: sel ? 1000 : 0 });
    m.on("click", () => selectBranch(branch));
    markersLayer.addLayer(m);
  });
}

watch(filterPanelOpen, open => {
  if (open && props.groups.length && !fpActiveGroup.value) fpActiveGroup.value = props.groups[0];
});

// Re-render markers whenever the visible set changes — pan, zoom, search,
// category filter, branch list reload all funnel through this single watcher.
watch(visibleBranches, () => { if (leafletMap) renderMarkers(); }, { deep: false });

// Measure the global AppFooter and expose its actual height as a CSS var
// on the .hmv element. The card uses this for its `bottom:` so it always
// sits flush against whatever the footer actually renders as — no math.
let footerObserver = null;
function syncFooterHeight() {
  const footerEl = document.querySelector(".fixed-bottom");
  const hmvEl    = mapEl.value?.parentElement; // .hmv
  if (!footerEl || !hmvEl) return;
  const h = footerEl.offsetHeight || 70;
  footerHeight.value = h;
  hmvEl.style.setProperty("--app-footer-h", `${h}px`);
}

function observeFooter() {
  if (footerObserver) { footerObserver.disconnect(); footerObserver = null; }
  syncFooterHeight();
  const footerEl = document.querySelector(".fixed-bottom");
  if (footerEl && typeof ResizeObserver !== "undefined") {
    footerObserver = new ResizeObserver(syncFooterHeight);
    footerObserver.observe(footerEl);
  }
}

onMounted(async () => {
  await nextTick();
  await initMapSaudi();
  tryLocationSilently();

  observeFooter();
  window.addEventListener("resize", syncFooterHeight);
});

// KeepAlive re-entry: the footer element is recreated after routes that hide
// it, so re-measure/re-observe; also make sure any stray body scroll is reset
// and Leaflet re-checks its container size.
onActivated(() => {
  window.scrollTo(0, 0);
  observeFooter();
  leafletMap?.invalidateSize();
});

onUnmounted(() => {
  if (leafletMap) { leafletMap.off("moveend zoomend", onMapMove); leafletMap.remove(); leafletMap = null; }
  clearTimeout(moveTimeout);
  clearTimeout(queryTimer);
  if (footerObserver) { footerObserver.disconnect(); footerObserver = null; }
  window.removeEventListener("resize", syncFooterHeight);
});
</script>

<style scoped lang="scss">
.hmv {
  // Fixed to the viewport (like SearchView's .sv) — prevents the mobile
  // keyboard from scrolling the page and leaving the layout displaced.
  // The AppFooter is position:fixed and floats over with backdrop blur.
  position: fixed;
  top: 0; bottom: 0;
  left: var(--app-gutter); right: var(--app-gutter);
  overflow: hidden;
  direction: rtl;
  z-index: 1; // below AppFooter (z:1030)

  // ── Map fills entire container ────────────────
  &__map {
    position: absolute; inset: 0;
    z-index: 1; // lowest — everything else stacks above

    :deep(.leaflet-tile-pane)   { filter: saturate(0.82) contrast(0.95) brightness(0.97); }
    :deep(.leaflet-control-attribution) { display: none; }
    :deep(.leaflet-container)   { background: #e8e4dc; }
    :deep(.marker-cluster-small),
    :deep(.marker-cluster-medium),
    :deep(.marker-cluster-large) {
      background: transparent !important;
      div { background: transparent !important; }
    }
  }

  // ── Top overlay: avatar (right) + search bar ─
  &__top {
    position: absolute;
    top: max(12px, env(safe-area-inset-top, 12px)); // clear the notch in standalone/PWA
    left: 12px; right: 12px;
    z-index: 11;
    display: flex; align-items: center; gap: 8px;
    direction: rtl;
  }

  // Profile button — must read as a button on top of busy map tiles:
  // solid espresso disc, gold ring, drop shadow (same height as the search bar).
  &__avatar {
    position: relative;
    flex-shrink: 0;
    width: 40px; height: 40px;
    border-radius: 50%;
    background: rgba(26, 22, 18, 0.96);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1.5px solid rgba(226, 182, 115, 0.70);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
    color: var(--color-signal);
    display: flex; align-items: center; justify-content: center;
    font-family: var(--font-display);
    font-weight: 700; font-size: 0.95rem;
    text-decoration: none;
    transition: all 0.15s;
    i { font-size: 0.85rem; }

    &:hover, &:active {
      border-color: var(--color-signal);
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45), 0 0 0 3px var(--color-signal-soft);
    }
  }

  // Unread-notifications dot on the leading (left in RTL) corner
  &__avatar-dot {
    position: absolute; top: -2px; left: -2px;
    width: 12px; height: 12px; border-radius: 50%;
    background: var(--color-closed, #D86545);
    border: 2px solid rgba(26, 22, 18, 0.96);
  }

  &__search {
    flex: 1; min-width: 0;
    display: flex; align-items: center; gap: 6px;
    height: 40px; padding: 0 12px;
    background: rgba(26, 22, 18, 0.92);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(247, 241, 230, 0.14);
    border-radius: var(--radius-full);
    transition: border-color 0.15s, box-shadow 0.15s;

    &--focus {
      border-color: var(--color-signal);
      box-shadow: 0 0 0 3px var(--color-signal-soft);
    }
  }

  &__search-icon {
    color: rgba(247, 241, 230, 0.45);
    font-size: 0.85rem; flex-shrink: 0;
  }

  &__search-input {
    flex: 1; min-width: 0;
    background: transparent; border: none; outline: none;
    color: #F7F1E6; font-family: var(--font-primary);
    font-size: 0.85rem;
    direction: rtl;
    &::placeholder { color: rgba(247, 241, 230, 0.40); }
    &::-webkit-search-cancel-button { display: none; }
  }

  &__search-clear {
    flex-shrink: 0;
    width: 22px; height: 22px; border-radius: 50%;
    border: none; background: rgba(247, 241, 230, 0.10);
    color: rgba(247, 241, 230, 0.65);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.65rem;
    &:hover { background: rgba(247, 241, 230, 0.18); color: #F7F1E6; }
  }

  // ── Category pills — below the top bar ────────
  &__pills {
    position: absolute;
    top: calc(max(12px, env(safe-area-inset-top, 12px)) + 52px);
    left: 12px; right: 60px;
    z-index: 10;
    display: flex; gap: 6px; overflow-x: auto;
    scrollbar-width: none; &::-webkit-scrollbar { display: none; }
  }

  &__pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 7px 13px;
    background: rgba(26, 22, 18, 0.88);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(247, 241, 230, 0.12);
    border-radius: var(--radius-full);
    color: rgba(247, 241, 230, 0.65);
    font-family: var(--font-primary); font-size: 0.75rem; font-weight: 600;
    white-space: nowrap; flex-shrink: 0; cursor: pointer;
    transition: all var(--transition-base);

    &--on {
      background: var(--color-signal); border-color: var(--color-signal); color: #1a1410;
      .hmv__pill__count { background: rgba(26,20,16,0.20); color: #1a1410; }
    }
    &__count {
      background: rgba(247, 241, 230, 0.12); color: rgba(247, 241, 230, 0.55);
      font-family: var(--font-mono); font-size: 0.65rem; font-weight: 700;
      padding: 1px 6px; border-radius: var(--radius-full);
    }
  }

  // ── Right controls stack — below the top bar ──
  &__controls {
    position: absolute;
    top: calc(max(12px, env(safe-area-inset-top, 12px)) + 52px);
    right: 12px;
    z-index: 10;
    display: flex; flex-direction: column; gap: 8px;
    align-items: center;
  }

  // Filter circle
  &__filter {
    position: relative; // for badge only
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(26, 22, 18, 0.88);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(247, 241, 230, 0.15);
    color: rgba(247, 241, 230, 0.65);
    font-size: 0.85rem; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
    &--on { border-color: var(--color-signal); color: var(--color-signal); }

    &__badge {
      position: absolute; top: -4px; right: -4px;
      width: 16px; height: 16px; border-radius: 50%;
      background: var(--color-signal); color: #1a1410;
      font-family: var(--font-mono); font-size: 0.55rem; font-weight: 800;
      display: flex; align-items: center; justify-content: center;
      border: 1.5px solid rgba(26, 22, 18, 0.88);
    }
  }

  // Count chip
  &__count {
    display: flex; align-items: center; gap: 4px;
    padding: 5px 10px;
    background: rgba(26, 22, 18, 0.88);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(247, 241, 230, 0.12);
    border-radius: var(--radius-full);
    font-size: 0.70rem; font-weight: 600; font-family: var(--font-mono);
    color: rgba(247, 241, 230, 0.65);
    white-space: nowrap;
    i { color: var(--color-signal); font-size: 0.62rem; }
  }

  // Locate me
  &__locate {
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(26, 22, 18, 0.88);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(247, 241, 230, 0.15);
    color: rgba(247, 241, 230, 0.65);
    font-size: 0.90rem; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
    &:hover { border-color: var(--color-signal); color: var(--color-signal); }
  }

  // ── Empty-area hint ───────────────────────────
  &__empty-hint {
    position: absolute;
    bottom: calc(var(--app-footer-h, 70px) + 20px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 16px;
    background: rgba(26, 22, 18, 0.92);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(247, 241, 230, 0.16);
    border-radius: var(--radius-full);
    color: rgba(247, 241, 230, 0.80);
    font-family: var(--font-primary);
    font-size: 0.78rem; font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    transition: border-color 0.15s;

    i { color: var(--color-signal); font-size: 0.82rem; }

    &__cta {
      color: var(--color-signal);
      font-weight: 800;
    }

    &:hover { border-color: var(--color-signal); }
  }

  // ── Single card — only when a place is selected ──
  // Sits flush against the AppFooter using --app-footer-h (measured in JS).
  &__card-wrap {
    position: absolute;
    left: var(--page-padding-x);
    right: var(--page-padding-x);
    bottom: calc(var(--app-footer-h, 70px) + 8px);
    z-index: 10;
    display: flex; align-items: stretch;
    transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1);
    will-change: transform;
    touch-action: pan-y; // allow vertical scroll, capture horizontal swipe

    &--dragging { transition: none; }
  }

  &__card-single {
    flex: 1; min-width: 0;
    display: flex; gap: 10px;
    background: rgba(19, 16, 13, 0.96);
    backdrop-filter: blur(28px);
    border: 1px solid rgba(247, 241, 230, 0.10);
    border-radius: var(--radius-lg);
    padding: 10px;
    cursor: pointer;
    transition: border-color 0.15s, transform 0.15s;
    &:hover  { border-color: var(--color-signal); }
    &:active { transform: scale(0.99); }

    .hmv__card-img {
      flex-shrink: 0;
      width: 64px; height: 64px;
      border-radius: var(--radius-md); overflow: hidden;
      background: rgba(26, 22, 18, 0.80);
      img { width: 100%; height: 100%; object-fit: cover; display: block; }
    }

    .hmv__card-info {
      flex: 1; min-width: 0;
      display: flex; flex-direction: column; gap: 4px;
    }

    .hmv__card-top {
      display: flex; align-items: center; justify-content: space-between; gap: 8px;
    }

    .hmv__card-name {
      margin: 0; font-family: var(--font-display);
      font-size: 0.95rem; font-weight: 800; color: #F7F1E6;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      // Roomy line box so the dots of a final ي don't get clipped
      line-height: 1.5; padding-bottom: 1px;
    }

    .hmv__card-renewed {
      flex-shrink: 0;
      font-size: 0.6rem; font-weight: 700; font-family: var(--font-mono);
      padding: 2px 7px; border-radius: var(--radius-full);
      background: rgba(226,182,115,0.2); color: #E2B673;
      border: 1px solid rgba(226,182,115,0.4);
    }

    .hmv__card-status {
      flex-shrink: 0;
      font-size: 0.62rem; font-weight: 700; font-family: var(--font-mono);
      padding: 2px 7px; border-radius: var(--radius-full);
      &--open   { background: rgba(143,163,122,0.22); color: #b8d4a4; border: 1px solid rgba(143,163,122,0.40); }
      &--closed { background: rgba(216,101,69,0.22);  color: #f0a888; border: 1px solid rgba(216,101,69,0.40); }
    }

    .hmv__card-district {
      margin: 0; font-size: 0.72rem; color: rgba(247,241,230,0.55);
      display: flex; align-items: center; gap: 4px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      i { font-size: 0.62rem; color: rgba(247,241,230,0.35); }
    }

    .hmv__card-foot {
      display: flex; align-items: center; justify-content: space-between;
      gap: 8px; margin-top: auto;
    }

    .hmv__card-dist {
      display: inline-flex; align-items: center; gap: 4px;
      font-family: var(--font-mono); font-size: 0.66rem; font-weight: 600;
      color: var(--color-signal);
      i { font-size: 0.58rem; }
    }

    .hmv__card-counter {
      font-family: var(--font-mono); font-size: 0.62rem;
      color: rgba(247,241,230,0.40);
    }
  }
}

// ── Card transition (Vue <Transition>) ─────────
.card-up-enter-active,
.card-up-leave-active {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1),
              opacity   0.20s ease;
}
.card-up-enter-from,
.card-up-leave-to {
  transform: translateY(120%);
  opacity: 0;
}

.hmv {
  // ── Filter panel ──────────────────────────────
  &__fp {
    position: absolute; inset: 0; z-index: 20;
    display: flex; flex-direction: column; justify-content: flex-end;
    background: rgba(11, 9, 7, 0.55);
    backdrop-filter: blur(4px);
  }

  &__fp-sheet {
    background: var(--color-panel);
    border-radius: 22px 22px 0 0;
    border-top: 1px solid var(--color-border);
    max-height: 72dvh;
    display: flex; flex-direction: column; overflow: hidden;
  }

  &__fp-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px var(--page-padding-x) 12px;
    border-bottom: 1px solid var(--color-border); flex-shrink: 0;
  }

  &__fp-title {
    font-family: var(--font-display); font-size: 1rem; font-weight: 800; color: var(--color-text-1);
    b { color: var(--color-signal); margin-right: 6px; }
  }

  &__fp-close {
    width: 32px; height: 32px; border-radius: 50%;
    border: 1px solid var(--color-border); background: var(--color-card);
    color: var(--color-text-3); font-size: 0.80rem; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    &:hover { border-color: var(--color-signal); color: var(--color-signal); }
  }

  &__fp-mode {
    display: flex; gap: 6px; padding: 10px var(--page-padding-x); flex-shrink: 0;
  }

  &__fp-mode-btn {
    flex: 1; padding: 9px; border-radius: var(--radius-md);
    border: 1px solid var(--color-border); background: var(--color-card);
    color: var(--color-text-3); font-family: var(--font-primary);
    font-size: 0.78rem; font-weight: 600; cursor: pointer;
    transition: all var(--transition-base);
    &--on { background: var(--color-signal-soft); border-color: var(--color-signal); color: var(--color-signal); }
  }

  &__fp-body { flex: 1; min-height: 0; display: flex; overflow: hidden; }

  &__fp-groups {
    width: 110px; flex-shrink: 0; overflow-y: auto; scrollbar-width: none;
    border-left: 1px solid var(--color-border); padding: var(--space-2) 0;
    &::-webkit-scrollbar { display: none; }
  }

  &__fp-group {
    display: flex; flex-direction: column; align-items: center;
    gap: 4px; padding: 12px var(--space-2); width: 100%;
    background: none; border: none; cursor: pointer;
    position: relative; transition: background 0.15s;
    &:hover { background: var(--color-card); }
    &--on { background: var(--color-signal-soft); }

    &__badge {
      position: absolute; top: 6px; right: 8px;
      width: 16px; height: 16px; border-radius: 50%;
      background: var(--color-signal); color: #1a1410;
      font-family: var(--font-mono); font-size: 0.56rem; font-weight: 800;
      display: flex; align-items: center; justify-content: center;
    }
    &__icon { font-size: 1.15rem; line-height: 1; }
    &__name {
      font-size: 0.64rem; font-weight: 600; color: var(--color-text-3); text-align: center; line-height: 1.3;
      .hmv__fp-group--on & { color: var(--color-signal); }
    }
  }

  &__fp-cats {
    flex: 1; overflow-y: auto; scrollbar-width: none; padding: var(--space-3);
    display: flex; flex-wrap: wrap; align-content: flex-start; gap: var(--space-2);
    &::-webkit-scrollbar { display: none; }
  }

  &__fp-hint {
    width: 100%; text-align: center; padding: var(--space-5);
    font-size: 0.78rem; color: var(--color-text-4); font-family: var(--font-mono); margin: 0;
  }

  &__fp-cat {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 8px 14px; border-radius: var(--radius-full);
    border: 1px solid var(--color-border); background: var(--color-card);
    color: var(--color-text-2); font-family: var(--font-primary);
    font-size: 0.78rem; font-weight: 500; cursor: pointer;
    transition: all var(--transition-base); white-space: nowrap;
    &--on { background: var(--color-signal-soft); border-color: var(--color-signal); color: var(--color-signal); font-weight: 700; }
  }

  &__fp-foot {
    display: flex; align-items: center; justify-content: flex-end; gap: var(--space-3);
    padding: var(--space-3) var(--page-padding-x);
    padding-bottom: max(var(--space-3), env(safe-area-inset-bottom, 12px));
    border-top: 1px solid var(--color-border); background: var(--color-card); flex-shrink: 0;
  }

  &__fp-reset {
    font-size: 0.78rem; color: var(--color-text-3); background: none; border: none;
    cursor: pointer; font-family: var(--font-primary); padding: 8px 12px;
    &:hover { color: var(--color-text-1); }
  }

  &__fp-apply {
    display: flex; align-items: center; gap: var(--space-2);
    padding: 11px var(--space-5);
    background: linear-gradient(135deg, var(--color-signal), var(--color-gold-deep, #C29250));
    border: none; border-radius: var(--radius-full);
    color: #1a1410; font-family: var(--font-primary);
    font-size: 0.82rem; font-weight: 800; cursor: pointer; box-shadow: var(--shadow-signal);
    &:hover { opacity: 0.9; }
    &__n {
      background: rgba(26,20,16,0.20); color: #1a1410;
      font-family: var(--font-mono); font-size: 0.68rem; font-weight: 800;
      padding: 1px 6px; border-radius: var(--radius-full);
    }
  }
}

// Transitions
.sheet-up-enter-active, .sheet-up-leave-active { transition: opacity 0.25s, transform 0.3s cubic-bezier(0.32,0.72,0,1); }
.sheet-up-enter-from, .sheet-up-leave-to       { opacity: 0; transform: translateY(20px); }
.pop-enter-active, .pop-leave-active           { transition: opacity 0.2s, transform 0.2s; }
.pop-enter-from, .pop-leave-to                 { opacity: 0; transform: translateX(-50%) translateY(-8px); }
</style>